/**
 * PatientSummaryCards — the three muted tinted summary cards of a patient: Problems and allergies (amber),
 * Medications (green), Documents (blue). One shared piece for the two places that draw them: the Overview hub
 * (`?x=patient-pages`) and the top of the patient record's one-page chart (Antonin, 19 Oct: "the chart starts with
 * the three summary cards"). Returns the cards as siblings, so the host's own grid lays them out.
 *
 * Every number and name is a field of a row an existing hook returns (no new endpoint, nothing invented):
 *   Problems and allergies `usePatientConditions('Active')`, `usePatientAllergies`
 *   Medications            `usePatientMedications(id, true)`
 *   Documents              `usePatientDocuments` (first page)
 * The prototype's Supplements, Wearables and Care team have no production data source: a gap, not drawn.
 * The hooks share their query keys with the chart's own sections, so a card and its section read one cache entry.
 */
import { Icon } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientAllergies } from '@/patients/services/use-patient-allergies';
import { usePatientConditions } from '@/patients/services/use-patient-conditions';
import { usePatientDocuments } from '@/patients/services/use-patient-documents';
import { usePatientMedications } from '@/patients/services/use-patient-medications';
import { firstLabels, liveRows } from '@/patients/lib/overview-headlines';
import { OverviewBlock, OverviewHeadline } from './overview-block';

export const SUMMARY_DOCUMENTS_PAGE_SIZE = 20;

export type SummaryCardTarget = 'problems' | 'medications' | 'documents';

export interface PatientSummaryCardsProps {
  patientId: string;
  /** A card's link: the chart section it names. */
  onOpen: (section: SummaryCardTarget) => void;
  /** Inside the chart the cards sit above their own sections, so every link reads "View" (the Overview hub names
   *  where each goes: "Full chart", "Medications", "Documents"). */
  shortLinks?: boolean;
}

export function PatientSummaryCards({ patientId, onOpen, shortLinks = false }: PatientSummaryCardsProps) {
  const problemsQ = usePatientConditions(patientId, 'Active');
  const allergiesQ = usePatientAllergies(patientId);
  const medsQ = usePatientMedications(patientId, true);
  const docsQ = usePatientDocuments({ patientId, pageSize: SUMMARY_DOCUMENTS_PAGE_SIZE });

  const problems = liveRows(problemsQ.data);
  const allergies = liveRows(allergiesQ.data);
  const meds = liveRows(medsQ.data);
  const docs = liveRows(docsQ.data);

  return (
    <>
      <OverviewBlock
        title="Problems and allergies"
        tint="amber"
        icon={<Icon name="heart" size={16} color="currentColor" />}
        linkLabel={shortLinks ? 'View' : 'Full chart'}
        onOpen={() => onOpen('problems')}
      >
        <BlockState loading={problemsQ.isLoading || allergiesQ.isLoading} error={problemsQ.isError || allergiesQ.isError}>
          <OverviewHeadline
            count={problems.length + allergies.length}
            noun={`active (${problems.length} problems, ${allergies.length} allergies)`}
            items={firstLabels([...problems.map((p) => p.code?.display), ...allergies.map((a) => a.code?.display)])}
            emptyText="No active problems or allergies"
          />
        </BlockState>
      </OverviewBlock>

      <OverviewBlock
        title="Medications"
        tint="green"
        icon={<Icon name="pill" size={16} color="currentColor" />}
        linkLabel={shortLinks ? 'View' : 'Medications'}
        onOpen={() => onOpen('medications')}
      >
        <BlockState loading={medsQ.isLoading} error={medsQ.isError}>
          <OverviewHeadline
            count={meds.length}
            noun="active"
            items={firstLabels(meds.map((m) => m.code?.display))}
            emptyText="No active medications"
          />
        </BlockState>
      </OverviewBlock>

      <OverviewBlock
        title="Documents"
        tint="blue"
        icon={<Icon name="file" size={16} color="currentColor" />}
        linkLabel={shortLinks ? 'View' : 'Documents'}
        onOpen={() => onOpen('documents')}
      >
        <BlockState loading={docsQ.isLoading} error={docsQ.isError}>
          <OverviewHeadline
            count={docs.length}
            countText={docs.length >= SUMMARY_DOCUMENTS_PAGE_SIZE ? `${SUMMARY_DOCUMENTS_PAGE_SIZE}+` : undefined}
            noun="documents"
            items={firstLabels(docs.map((d) => d.name))}
            emptyText="No documents"
          />
        </BlockState>
      </OverviewBlock>
    </>
  );
}

function BlockState({ loading, error, children }: { loading: boolean; error: boolean; children: React.ReactNode }) {
  if (loading) return <Skeleton className="h-10 w-full" />;
  if (error) return <p data-slot="overview-empty">Could not load. Try again later.</p>;
  return <>{children}</>;
}
